import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { createEngine, emptyState, GRID_SIZE } from "../lib/engine";
import { createUndoStack } from "../lib/undo";
import { dimsOf as dimsBy, occupied as occupiedBy } from "../lib/editorGeom";
import { Toaster, toast } from "sonner";
import { Search } from "lucide-react";
import { C, FONT_HEAD, FONT_BODY } from "./editor/theme";
import { Category } from "./editor/Catalog";
import { ToolsDock, ZoomDock } from "./editor/Docks";
import { EditorHeader, SKY_MODES } from "./editor/EditorHeader";
import { EditorHints } from "./editor/EditorHints";
import { Inspector } from "./editor/Inspector";
import { useEditorInput } from "./editor/useEditorInput";
export { C, FONT_HEAD, FONT_BODY };

// Editor kota (walikota & admin). Bagian: editor/{EditorHeader,EditorHints,Inspector,Catalog,Docks,useEditorInput}
export default function CityEditor({ title, loadState, saveState, onLogout, headerExtra, isAdmin = false, token = null }) {
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const undoRef = useRef(createUndoStack(25));
  const [state, setState] = useState(null);
  const [defs, setDefs] = useState([]);
  const [tool, setTool] = useState(null);        // def bangunan yang dipilih
  const [toolVid, setToolVid] = useState(null);  // varian bangunan yang dipilih (null = versi aktif)
  const [mode, setMode] = useState("build");     // build | road | water | erase
  const [roadWidth, setRoadWidth] = useState(1);
  const [hover, setHover] = useState(null);
  const [sel, setSel] = useState(null);          // index bangunan terpilih (inspector)
  const [moving, setMoving] = useState(null);    // index bangunan yang sedang dipindah
  const [query, setQuery] = useState("");
  const [showGrid, setShowGrid] = useState(false);
  const [skyMode, setSkyMode] = useState("auto"); // auto | day | night | rain
  const [animOn, setAnimOn] = useState(true);     // animasi kehidupan (awan, burung, ikan, perahu, kendaraan, warga)
  const [signDraft, setSignDraft] = useState(null); // draf edit huruf timbul utk bangunan terpilih

  useEffect(() => {
    Promise.all([loadState(), api.catalog()])
      .then(([s, c]) => {
        setState(s || emptyState());
        setDefs(c.buildings || []);
      })
      .catch(e => toast.error(e.message));
  }, [loadState]);

  const dimsOf = (b) => dimsBy(defs, b);
  const occupied = (list, gx, gy, w, h, ex, bid) => occupiedBy(defs, list, gx, gy, w, h, ex, bid);

  // Ghost footprint saat mode bangun (pilih tool) atau sedang memindah (tool Pindah)
  const ghost = useMemo(() => {
    if (!state || !hover) return null;
    let w, h, excludeIdx = -1, bid = null;
    if (moving !== null) {
      const mb = state.buildings[moving];
      if (!mb) return null;
      [w, h] = dimsOf(mb); excludeIdx = moving; bid = mb.bid;
    } else if (mode === "build" && tool) {
      w = tool.w; h = tool.h; bid = tool.bid;
    } else return null;
    const [gx, gy] = hover;
    if (gx < 0 || gy < 0 || gx >= GRID_SIZE || gy >= GRID_SIZE) return null;
    const valid = gx + w <= GRID_SIZE && gy + h <= GRID_SIZE && !occupied(state.buildings, gx, gy, w, h, excludeIdx, bid);
    return { x: gx, y: gy, w, h, valid };
  }, [state, defs, mode, tool, hover, moving]); // eslint-disable-line

  const doRender = React.useCallback((st = state, opts = {}) => {
    if (!engineRef.current || !st) return;
    engineRef.current.render(st, defs, { hover, erase: mode === "erase", ghost, showGrid, ...opts });
  }, [state, defs, hover, mode, ghost, showGrid]);

  useEffect(() => {
    if (!state || !canvasRef.current) return;
    if (!engineRef.current) {
      engineRef.current = createEngine(canvasRef.current);
      engineRef.current.fitView();
    }
    doRender();
  }, [doRender, state]);

  // Escape: batalkan tool/pindah/seleksi
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") { setTool(null); setToolVid(null); setMoving(null); setSel(null); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function commit(next) {
    undoRef.current.push(state);
    setState(next);
  }

  // Putar bangunan 90° searah jarum jam (tolak jika ruang tidak cukup)
  function rotateBuildingAt(idx) {
    const b = state.buildings[idx];
    const def = defs.find(d => d.bid === b.bid);
    if (!def) return;
    const rot = ((b.rot || 0) + 1) % 4;
    let w = def.w || 1, h = def.h || 1;
    if (rot % 2 === 1) [w, h] = [h, w];
    if (b.x + w > GRID_SIZE || b.y + h > GRID_SIZE || occupied(state.buildings, b.x, b.y, w, h, idx, b.bid)) {
      toast.error("Tidak bisa diputar — ruang tidak cukup");
      return;
    }
    commit({ ...state, buildings: state.buildings.map((bb, i) => i === idx ? { ...bb, rot } : bb) });
    toast.success("Bangunan diputar 90°");
  }

  const input = useEditorInput({ state, setState, defs, mode, tool, toolVid, moving, setMoving, setSel, setHover, ghost, roadWidth, commit, engineRef, canvasRef, undoRef, doRender, rotateBuildingAt });

  // Pindah tool di dock melayang — memilih bangunan dari katalog tetap otomatis ke mode Bangun
  function selectMode(m) {
    input.resetDraw();
    setMode(m);
    if (m !== "build") { setTool(null); setToolVid(null); }
    if (m !== "move") setMoving(null);
    setSel(null);
  }

  function undo() {
    const prev = undoRef.current.undo(state);
    if (prev) { setState(prev); setSel(null); setMoving(null); }
  }
  function redo() {
    const next = undoRef.current.redo(state);
    if (next) { setState(next); setSel(null); setMoving(null); }
  }

  async function save() {
    // Gedung konstruksi wajib punya jadwal (durasi + bangunan lanjutan) sebelum kota disimpan
    const tanpaJadwal = (state.buildings || []).filter(b => defs.find(d => d.bid === b.bid)?.konstruksi && !b.konstruksi?.selesai_at);
    if (tanpaJadwal.length) { toast.error(`${tanpaJadwal.length} gedung Under Construction belum diatur jadwalnya — pilih gedungnya lalu isi lama pembangunan & bangunan lanjutan`); return; }
    try {
      await saveState(state);
      toast.success("Tersimpan ✓");
    } catch (e) { toast.error(e.message); }
  }

  function deleteSelected() {
    if (sel === null) return;
    commit({ ...state, buildings: state.buildings.filter((_, i) => i !== sel) });
    setSel(null);
    toast.success("Bangunan dihapus");
  }

  function pickTool(d) {
    setTool(prev => prev?.bid === d.bid ? null : d);
    setToolVid(null);
    setMode("build");
    setSel(null);
    setMoving(null);
  }

  // Posisi inspector popover di layar
  const selInfo = useMemo(() => {
    if (sel === null || !state || !engineRef.current) return null;
    const b = state.buildings[sel];
    if (!b) return null;
    const def = defs.find(d => d.bid === b.bid);
    const [sx, sy] = engineRef.current.gridToScreen(b.x + (def?.w || 1), b.y + (def?.h || 1));
    return { b, def, x: sx, y: sy };
  }, [sel, state, defs, hover]); // eslint-disable-line

  // Filter + grouping katalog
  const CATS = ["Perumahan", "Komersial", "Industri", "Publik", "Infrastruktur", "Ibadah", "Transportasi", "Hiburan", "Landmark", "Alam", "Sponsor"];
  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q ? defs.filter(d => d.name.toLowerCase().includes(q)) : defs;
    const map = {};
    for (const c of CATS) map[c] = [];
    for (const d of filtered) (map[d.category] = map[d.category] || []).push(d);
    return CATS.filter(c => map[c]?.length).map(c => [c, map[c]]);
  }, [defs, query]); // eslint-disable-line

  useEffect(() => { engineRef.current?.setSkyMode(skyMode); }, [skyMode, state]);
  useEffect(() => { engineRef.current?.setAnimasi(animOn); }, [animOn, state]);
  const cycleSky = () => setSkyMode(SKY_MODES[(SKY_MODES.findIndex(m => m[0] === skyMode) + 1) % SKY_MODES.length][0]);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", background: C.page, color: C.ink, fontFamily: FONT_BODY, overflow: "hidden" }}>
      <Toaster theme="light" position="bottom-center" />
      <EditorHeader headerExtra={headerExtra} animOn={animOn} onToggleAnim={() => setAnimOn(v => !v)} skyMode={skyMode} onCycleSky={cycleSky}
        onUndo={undo} onRedo={redo} showGrid={showGrid} onToggleGrid={() => setShowGrid(g => !g)} onLogout={onLogout} onSave={save} />

      <div style={{ flex: 1, display: "flex", minHeight: 0, gap: 14, padding: "0 18px 16px" }}>
        {/* SIDEBAR — kartu putih rounded */}
        <aside style={{ width: 350, flex: "none", background: "#fff", borderRadius: 24, boxShadow: "0 8px 22px rgba(20,20,20,0.06)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ padding: "14px 16px 12px", borderBottom: `1px solid ${C.line}` }}>
            <div style={{ position: "relative" }}>
              <Search size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: C.t3 }} />
              <input data-testid="catalog-search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Cari gedung..."
                style={{ width: "100%", padding: "10px 12px 10px 35px", background: C.hoverBg, border: `1.5px solid ${C.line}`, borderRadius: 99, color: C.ink, fontSize: 13, outline: "none", fontFamily: FONT_BODY }} />
            </div>
            <EditorHints mode={mode} tool={tool} toolVid={toolVid} onToolVid={setToolVid} moving={moving} />
          </div>
          {/* Katalog */}
          <div data-testid="catalog-panel" style={{ flex: 1, overflowY: "auto" }}>
            {grouped.map(([cat, items], i) => (
              <Category key={cat} name={cat} items={items} tool={tool} onPick={pickTool} defaultOpen={i === 0 || query.trim().length > 0} forceOpen={query.trim().length > 0} />
            ))}
            {defs.length === 0 && <p style={{ padding: 16, fontSize: 13, color: C.t3 }}>Katalog kosong.</p>}
          </div>
        </aside>

        {/* CANVAS — kartu rounded dengan langit terang */}
        <div style={{ flex: 1, position: "relative", minWidth: 0, background: C.sky, borderRadius: 24, overflow: "hidden", boxShadow: "0 8px 22px rgba(20,20,20,0.06)" }}>
          <canvas ref={canvasRef} data-testid="editor-canvas" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", cursor: mode === "pan" ? "grab" : mode === "move" ? "move" : mode === "erase" ? "not-allowed" : "default" }}
            onClick={input.onClick} onMouseMove={(e) => { input.onMove(e); input.onMouseMove(e); }}
            onMouseDown={input.onMouseDown} onMouseUp={input.onMouseUp} onMouseLeave={input.onMouseUp} onContextMenu={input.onContextMenu}
            onWheel={input.onWheel} />

          <ToolsDock mode={mode} onMode={selectMode} roadWidth={roadWidth} onRoadWidth={setRoadWidth} />
          <ZoomDock onZoomIn={() => { engineRef.current?.zoomAt(1.2); doRender(); }} onZoomOut={() => { engineRef.current?.zoomAt(0.85); doRender(); }} onFit={() => { engineRef.current?.fitView(); doRender(); }} />

          {selInfo && mode === "build" && !tool && (
            <Inspector selInfo={selInfo} sel={sel} state={state} defs={defs} commit={commit} canvasRef={canvasRef} signDraft={signDraft} setSignDraft={setSignDraft} isAdmin={isAdmin} token={token}
              onMove={() => { setMoving(sel); setSel(null); setMode("move"); }} onRotate={() => rotateBuildingAt(sel)} onDelete={deleteSelected} />
          )}
        </div>
      </div>
    </div>
  );
}
